import { Fragment } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import { Paper, Typography, Grid, Divider, Box } from '@mui/material';
import { IDashboardSummary } from '../../../../../application/models';

interface TopConsumoSectionProps {
  categoryName: string;
  dailyData: IDashboardSummary[];
  monthlyData: IDashboardSummary[];
}

interface TopConsumoCardProps {
  periodTitle: string;
  categoryName: string;
  data: IDashboardSummary[];
}

const colorPalette = ['#9b59b6', '#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#e67e22', '#1abc9c', '#34495e', '#95a5a6', '#7f8c8d'];

function TopConsumoCard({ periodTitle, categoryName, data }: TopConsumoCardProps) {
  const rawData = data.filter(item => item && item.Nome && item.Quantidade && item.Quantidade > 0);

  const chartData = rawData.map((item, index) => ({
    value: item.Quantidade!,
    label: item.Produto ? `${item.Nome} - ${item.Produto}` : item.Cliente ? `${item.Nome} - ${item.Cliente}` : item.Nome!,
    id: index,
  }));

  const legendData = rawData.map((item, index) => ({
    label: item.Produto ? `${item.Nome} - ${item.Produto}` : item.Cliente ? `${item.Nome} - ${item.Cliente}` : item.Nome!,
    value: item.Quantidade!,
    color: colorPalette[index % colorPalette.length],
  }));

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Grid container style={{ width: '100%' }} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" fontWeight={'bold'} textAlign="center">
            {periodTitle}
          </Typography>
        </Grid>
      </Grid>

      <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%'
      }}>
        {chartData.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: '100%' }}>
            <PieChart
              colors={colorPalette}
              series={[
                {
                  highlightScope: { faded: 'series', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                  data: chartData,
                  valueFormatter: (item) => item.value.toLocaleString('pt-BR'),
                  innerRadius: 30,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  cx: 120,
                  cy: 150,
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
              width={300}
              height={350}
              margin={{ right: 5 }}
            />

            {/* Legenda customizada — independente do gráfico */}
            {(() => {
              const total = legendData.reduce((sum, item) => sum + item.value, 0);
              return (
                <Box sx={{ width: '100%', px: 1, mt: 'auto' }}>
                  {legendData.map((item, index) => (
                    <Fragment key={index}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          py: 1.2,
                          px: 1.2,
                          borderRadius: '4px',
                          backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'transparent'
                        }}
                      >
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontSize: 15, flex: 1, color: 'text.primary', fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                        <Box sx={{ textAlign: 'right', minWidth: 85 }}>
                          <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 'bold', color: 'primary.main', display: 'block' }}>
                            {item.value.toLocaleString('pt-BR')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 'bold' }}>
                            {total > 0 ? `${((item.value / total) * 100).toFixed(1)}%` : '0%'}
                          </Typography>
                        </Box>
                      </Box>
                    </Fragment>
                  ))}
                  <Divider sx={{ mt: 1, mb: 1, opacity: 0.8, borderBottomWidth: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', px: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontSize: 14, fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>TOTAL</Typography>
                    <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 'bold', color: 'primary.main', lineHeight: 1 }}>{total.toLocaleString('pt-BR')}</Typography>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        ) : (
          <Typography
            variant="h6"
            color="text.secondary"
            textAlign="center"
            sx={{
              fontStyle: 'italic',
              opacity: 0.7
            }}
          >
            Nenhum dado de consumo por {categoryName.toLowerCase()} disponível para este período
          </Typography>
        )}
      </div>
    </Paper>
  );
}

export function TopConsumoSection({ categoryName, dailyData, monthlyData }: TopConsumoSectionProps) {
  return (
    <Fragment>
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          mb: 3
        }}
      >
        <Grid container style={{ width: '100%' }} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h5" color="primary" fontWeight={'bold'} textAlign="center">
              TOP Consumo por {categoryName}
            </Typography>
          </Grid>
        </Grid>

        <Divider style={{ margin: '16px 0', borderBottomWidth: '2px', borderBottomColor: 'rgba(0,0,0,0.5)' }} />

        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <TopConsumoCard periodTitle="Diário" categoryName={categoryName} data={dailyData} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TopConsumoCard periodTitle="Mensal" categoryName={categoryName} data={monthlyData} />
          </Grid>
        </Grid>
      </Paper>
    </Fragment>
  );
}
